// ICSP-59: persistent bottom navigation for the three authenticated screens.
//
// Replaces the overloaded back-chevron nav loop (Chat <-> Account <-> Reco)
// with a fixed-position tab bar. Mounted once by AnimatedLayout (routes.tsx)
// as an in-flow column sibling — never `position: fixed` — so it reserves
// its own space and can't overlay screen content.
//
// Theming: colours come ONLY from the tenant-driven Tailwind tokens
// (`rame` / `paper` / `ink-faint`, all backed by :root CSS vars set in
// branding.ts). No hardcoded brand hex — ICS renders copper, LDF green,
// with zero per-tenant code. The hairline + translucency echo TopBar.

import { ClipboardList, MessageCircle, User } from "lucide-react";
import { NavLink } from "react-router-dom";

import { ROUTES } from "@/lib/paths";
import { cn } from "@/lib/utils";

export interface BottomNavProps {
  className?: string;
}

const TABS = [
  { to: ROUTES.chat, label: "Conversazione", Icon: MessageCircle },
  { to: ROUTES.recommendation, label: "Il mio piano", Icon: ClipboardList },
  { to: ROUTES.account, label: "Account", Icon: User },
] as const;

export function BottomNav({ className }: BottomNavProps) {
  return (
    <nav
      aria-label="Navigazione principale"
      className={cn(
        "flex shrink-0 items-stretch justify-around",
        "border-t border-[rgba(60,50,40,0.08)] bg-paper/90 backdrop-blur-md",
        // Lift the bar above the home indicator on notched devices; relies
        // on the viewport-fit=cover meta already present in index.html.
        "pb-[max(0.5rem,env(safe-area-inset-bottom))]",
        className,
      )}
    >
      {TABS.map(({ to, label, Icon }) => (
        <NavLink
          key={to}
          to={to}
          end
          className="group relative flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 pt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rame focus-visible:ring-inset"
        >
          {({ isActive }) => (
            <>
              {isActive && (
                <span
                  aria-hidden
                  className="absolute inset-x-0 top-0 h-0.5 bg-rame"
                />
              )}
              <Icon
                aria-hidden
                size={22}
                strokeWidth={isActive ? 2 : 1.6}
                className={isActive ? "text-rame" : "text-ink-faint"}
              />
              <span
                className={cn(
                  "text-[11px] leading-none",
                  isActive
                    ? "font-semibold text-rame"
                    : "font-medium text-ink-faint",
                )}
              >
                {label}
              </span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
